/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Render the roster with guardian state, selection bar and row menu.
 * @state-owner classroom UI store owns selection; dataset store owns roster and guardians
 * @allowed-imports React; dataset and workspace stores; classroom store; shared components and hooks
 * @forbidden direct API client; student-only surfaces
 * @public-api ClassDetail
 * @side-effects dialogs, context menus and navigation
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { MenuRows } from '../../../shared/components/ContextMenu';
import { pointerPoint, useOverlay } from '../../../shared/components/OverlayHost';
import { NameCell, StatusText } from '../../../shared/components/StatusIcon';
import { CheckCell, tableCells, VTable } from '../../../shared/components/VTable';
import {
  classMaterials,
  classRuns,
  findSeat,
  seatPresentation,
  seatStatusLabel,
} from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { AssignmentDialog } from '../../activities';
import { GUARDIAN_STATE_LABEL, guardianState, guardianStats } from '../model/guardian';
import { useClassroomUiStore } from '../store/classroom-ui-store';
import { GuardianBatchDialog } from './GuardianBatchDialog';
import { GuardianDialog } from './GuardianDialog';

export function ClassStudentsTab({ classIndex }: { classIndex: number }) {
  const t = useTranslator();
  const { openModal, openPopover } = useOverlay();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);
  const seats = useDatasetStore((state) => state.seats);
  const guardians = useDatasetStore((state) => state.guardians);
  const openItem = useWorkspaceUiStore((state) => state.openItem);
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const selected = useClassroomUiStore((state) => state.selectedStudents);
  const toggleStudent = useClassroomUiStore((state) => state.toggleStudent);
  const clearStudents = useClassroomUiStore((state) => state.clearStudents);

  const classroom = classes[classIndex];
  const live = classRuns(runs, classIndex).some((run) => run.status === 'live');
  const stats = guardianStats(classroom.students, guardians);
  const firstMaterial =
    classMaterials(activities, classIndex)[0] ??
    activities.find((activity) => activity.status === 'ready');

  return (
    <>
      <button
        className="gsum"
        type="button"
        onClick={() => openModal(<GuardianBatchDialog classIndex={classIndex} />)}
      >
        <span className="gsl">
          <b>{t('家長綁定')}</b> {`${stats.linked} / ${stats.total}`}
        </span>
        <span className="gbar sm">
          <i style={{ width: `${(stats.linked / stats.total) * 100}%` }} />
          <i className="pd" style={{ width: `${(stats.pending / stats.total) * 100}%` }} />
        </span>
        {stats.none ? <span className="vmut">{`${stats.none} ${t('人還沒收到邀請')}`}</span> : null}
        <div className="sp" />
        <span className="lk">{t('一次處理')}</span>
      </button>

      {selected.length ? (
        <div className="vselbar">
          <b>{`已選 ${selected.length} 位`}</b>
          <button className="bt sm" type="button" onClick={clearStudents}>
            {t('取消')}
          </button>
          <button
            className="bt sm pri"
            type="button"
            onClick={() =>
              openModal(
                <AssignmentDialog
                  activityId={firstMaterial?.id ?? 'a1'}
                  classIndex={classIndex}
                  preselected={selected}
                />,
              )
            }
          >
            {t('指派給這些人')}
          </button>
        </div>
      ) : null}

      <VTable
        empty={t('這個班還沒有學生')}
        head={['', t('學生'), t('狀態'), t('家長'), t('紀錄')]}
        rows={classroom.students.map((student) => {
          const seat = findSeat(seats[classIndex] ?? [], student);
          const look = seat ? seatPresentation(seat.status) : null;
          const hot = Boolean(live && look?.needsTeacher);
          const glyph = live && look ? look.glyph : 'todo';
          const guardian = GUARDIAN_STATE_LABEL[guardianState(guardians[student] ?? [])];
          return {
            key: student,
            on: selected.includes(student),
            onSelect: () => openItem({ k: 'stu', n: student }),
            onContextMenu: (event) => {
              event.preventDefault();
              event.stopPropagation();
              const point = pointerPoint(event);
              openPopover({
                x: point.x,
                y: point.y,
                className: 'cmenu rmenu',
                content: (
                  <MenuRows
                    items={[
                      {
                        label: t('開啟學生'),
                        shortcut: '↵',
                        onSelect: () => openItem({ k: 'stu', n: student }),
                      },
                      {
                        label: t('檢視學習報告'),
                        shortcut: 'R',
                        onSelect: () => navigate('rep', student),
                      },
                      'divider',
                      { label: t('勾選'), shortcut: 'X', onSelect: () => toggleStudent(student) },
                    ]}
                  />
                ),
              });
            },
            cells: tableCells(
              <CheckCell on={selected.includes(student)} onToggle={() => toggleStudent(student)} />,
              <NameCell icon={student[0]} text={student} tone={hot ? 'e' : ''} />,
              <StatusText
                glyph={glyph}
                label={live && seat ? seatStatusLabel(seat, t) : t('沒有進行中的活動')}
                tone={live && look ? look.tone : ''}
              />,
              <button
                className="gcell"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  openModal(<GuardianDialog student={student} />);
                }}
              >
                <StatusText glyph={guardian[1]} label={t(guardian[0])} tone={guardian[2]} />
              </button>,
              <span className="vmut">{`${classRuns(runs, classIndex).length} 份`}</span>,
            ),
          };
        })}
        template="26px minmax(0,1fr) 190px 150px 70px"
      />
    </>
  );
}
